import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Typography,
  Paper,
  Grid,
  Button,
  Box,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import CourierLayout from '../CourierLayout';
import { API } from '../../../config';

import CarrierFeeReviewDialog from './CarrierFeeReviewDialog';

const AvailableOrders = () => {
  const history = useHistory();

  const [orders, setOrders] = useState([]);
  const [bidDialogOpen, setBidDialogOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [bidAmount, setBidAmount] = useState('');
  const [bidNote, setBidNote] = useState('');
  const [submittingBid, setSubmittingBid] = useState(false);
  const [feeReview, setFeeReview] = useState(null);

  const token =
    localStorage.getItem('courier_token') ||
    localStorage.getItem('token');

  const fetchOrders = async () => {
    try {
      const response = await fetch(`${API}/api/orders/available`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      const taggedOrders = (data || []).map((order) => ({
        ...order,
        isRecommended:
          order.required_truck_type &&
          Number(order.distance_in_miles || 0) < 20,
      }));

      setOrders(taggedOrders);
    } catch (err) {
      console.error('Failed to fetch available loads:', err);
    }
  };

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    const events = new EventSource(`${API}/api/events`);

    const refresh = () => fetchOrders();

    events.addEventListener('offer_created', refresh);
    events.addEventListener('order_assigned', refresh);
    events.addEventListener('order_status', refresh);

    return () => events.close();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

    const handleAccept = (orderId) => {
    if (submittingBid || feeReview) return;
    setFeeReview({ action: 'accept', orderId: Number(orderId) });
  };

  const openBidDialog = (orderId) => {
    setSelectedOrderId(orderId);
    setBidAmount('');
    setBidNote('');
    setBidDialogOpen(true);
  };

  const closeBidDialog = () => {
    if (submittingBid || feeReview) return;
    setBidDialogOpen(false);
    setSelectedOrderId(null);
    setBidAmount('');
    setBidNote('');
  };

  // Review first. No bid is submitted by this function.
  const submitBid = () => {
    if (submittingBid || feeReview) return;

    const text = bidAmount.trim();

    if (!/^(?:\d+(?:\.\d{0,2})?|\.\d{1,2})$/.test(text)) {
      alert('Enter a positive price with no more than two decimal places.');
      return;
    }

    const [whole, fraction = ''] = text.split('.');
    const amountCents = Number((whole || '0') + fraction.padEnd(2, '0'));
    const orderId = Number(selectedOrderId);

    if (
      !Number.isSafeInteger(amountCents) || amountCents <= 0 ||
      !Number.isSafeInteger(orderId) || orderId <= 0
    ) {
      alert('Please enter a valid bid amount and select a load.');
      return;
    }

    setFeeReview({
      action: 'bid',
      orderId,
      amountCents,
      note: bidNote.trim(),
    });
  };

  // Called only after the carrier sees the estimate and checks the box.
  const confirmCarrierAction = async (carrierFeeAcceptance) => {
    const review = feeReview;

    if (!review || submittingBid || carrierFeeAcceptance?.accepted !== true) {
      throw new Error('Please review the fee breakdown again.');
    }

    const isBid = review.action === 'bid';

    if (isBid && carrierFeeAcceptance.amountCents !== review.amountCents) {
      throw new Error('The bid price changed. Please review it again.');
    }

    setSubmittingBid(true);

    try {
      const response = await fetch(
        isBid
          ? `${API}/api/orders/${review.orderId}/bids`
          : `${API}/api/orders/accept`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(isBid ? {
            amount_cents: review.amountCents,
            note: review.note,
            notes: review.note,
            carrierFeeAcceptance,
          } : {
            orderId: review.orderId,
            carrierFeeAcceptance,
          }),
        }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data.message || data.error?.message ||
          (typeof data.error === 'string' ? data.error : '') ||
          'Unable to submit. Please review and try again.'
        );
      }

      setFeeReview(null);

      if (isBid) {
        setBidDialogOpen(false);
        setSelectedOrderId(null);
        setBidAmount('');
        setBidNote('');
      } else {
        setOrders((prev) => prev.filter(
          (order) => Number(order.order_id) !== review.orderId
        ));
      }

      alert(isBid ? 'Bid submitted successfully.' : 'Load accepted successfully.');
    } finally {
      setSubmittingBid(false);
    }
  };

  const openNegotiation = async (orderId) => {
    try {
      const response = await fetch(`${API}/api/negotiations/order/${orderId}/thread`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Unable to open negotiation.');
      }

      const thread = await response.json();

      history.push(`/courier/negotiations/thread/${thread.id}`);
    } catch (err) {
      console.error('openNegotiation error:', err);
      alert(err.message || 'Unable to open negotiation.');
    }
  };

  return (
    <CourierLayout>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          sx={{
            fontSize: '2rem',
            fontWeight: 700,
            lineHeight: 1.25,
            color: '#0f172a',
            mb: 1,
            textAlign: 'center',
          }}
        >
          Available Loads
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: '#64748b',
            textAlign: 'center',
            fontSize: '0.95rem',
            lineHeight: 1.5,
          }}
        >
          Review available loads, place bids, negotiate rates, or accept assignments.
        </Typography>
      </Box>

      {orders.length === 0 ? (
        <Paper
          elevation={1}
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: 3,
            border: '1px solid rgba(15, 23, 42, 0.08)',
            textAlign: 'center',
            backgroundColor: '#ffffff',
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              color: '#0f172a',
              mb: 1,
            }}
          >
            No matching loads available.
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: '#64748b',
              fontSize: '0.95rem',
            }}
          >
            Check back later for new opportunities.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {orders.map((order) => (
            <Grid item xs={12} key={order.order_id}>
              <Paper
                elevation={1}
                sx={{
                  p: { xs: 2, md: 2.5 },
                  borderRadius: 3,
                  border: '1px solid rgba(15, 23, 42, 0.08)',
                  backgroundColor: '#ffffff',
                }}
              >
                <Box sx={{ mb: 1.5, textAlign: 'center' }}>
                  {order.isRecommended && (
                    <Chip
                      label="Recommended"
                      size="small"
                      sx={{
                        mb: 1,
                        backgroundColor: '#e0f2fe',
                        color: '#0369a1',
                        fontWeight: 600,
                      }}
                    />
                  )}

                  <Typography
                    variant="body1"
                    sx={{
                      color: '#0f172a',
                      fontWeight: 600,
                      lineHeight: 1.5,
                    }}
                  >
                    Pickup: {order.from_address || 'Not provided'}
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      color: '#0f172a',
                      fontWeight: 600,
                      lineHeight: 1.5,
                    }}
                  >
                    Drop-off: {order.to_address || 'Not provided'}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: '#475569',
                      fontSize: '0.95rem',
                      mt: 0.5,
                    }}
                  >
                    Service Type: {order.delivery_type || 'Standard'}
                  </Typography>

                  {order.required_truck_type && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#475569',
                        fontSize: '0.95rem',
                      }}
                    >
                      Vehicle Type: {order.required_truck_type}
                    </Typography>
                  )}

                  {order.distance_in_miles != null && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#475569',
                        fontSize: '0.95rem',
                      }}
                    >
                      Distance: {order.distance_in_miles} mi
                    </Typography>
                  )}

                  {Number.isFinite(order.ai_score) && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#2e7d32',
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        mt: 0.5,
                      }}
                    >
                      AI Match: {order.ai_score}
                    </Typography>
                  )}
                </Box>

                <Divider sx={{ my: 1.5 }} />

                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={4}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => handleAccept(order.order_id)}
                      fullWidth
                      sx={{
                        minHeight: 42,
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: 2,
                      }}
                    >
                      Accept Load
                    </Button>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={() => openBidDialog(order.order_id)}
                      fullWidth
                      sx={{
                        minHeight: 42,
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: 2,
                      }}
                    >
                      Place Bid
                    </Button>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => openNegotiation(order.order_id)}
                      fullWidth
                      sx={{
                        minHeight: 42,
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: 2,
                      }}
                    >
                      Negotiate
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog
        open={bidDialogOpen && !feeReview}
        onClose={closeBidDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 0.5,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            color: '#0f172a',
            pb: 1,
          }}
        >
          Place Bid
        </DialogTitle>

        <DialogContent>
          <Typography
            variant="body2"
            sx={{
              color: '#64748b',
              mb: 2,
              lineHeight: 1.5,
            }}
          >
            Enter your transportation price before processing fees. You will review the fee estimate and estimated earnings before submitting.
          </Typography>

          <TextField
            label="Bid Amount"
            placeholder="45.00"
            type="number"
            fullWidth
            value={bidAmount}
            onChange={(e) => setBidAmount(e.target.value)}
            inputProps={{
              min: '0',
              step: '0.01',
            }}
            sx={{ mb: 2 }}
          />

          <TextField
            label="Note"
            placeholder="Optional message"
            fullWidth
            multiline
            minRows={3}
            value={bidNote}
            onChange={(e) => setBidNote(e.target.value)}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={closeBidDialog}
            disabled={submittingBid}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={submitBid}
            variant="contained"
            disabled={submittingBid}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 2,
              minWidth: 120,
            }}
          >
            {submittingBid ? 'Submitting...' : 'Review Bid & Fees'}
          </Button>
        </DialogActions>
           </Dialog>

      {feeReview && (
        <CarrierFeeReviewDialog
          key={`${feeReview.action}-${feeReview.orderId}-${feeReview.amountCents ?? 'order'}`}
          token={token}
          orderId={feeReview.orderId}
          amountCents={feeReview.amountCents}
          confirmLabel={
            feeReview.action === 'bid'
              ? 'Confirm & Submit Bid'
              : 'Confirm & Accept Load'
          }
          onCancel={() => setFeeReview(null)}
          onConfirm={confirmCarrierAction}
        />
      )}
    </CourierLayout>
  );
};

export default AvailableOrders;